import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface VariantBrief {
    id: number;
    variantName: string;
}

export interface VariantDetail {
    id: number;
    variantDetailName: string;
    statisticName: string;
    sortOrder: number;
}

export interface VariantGroup {
    id: number;
    groupName: string;
    variant: VariantBrief;
    variantDetails: VariantDetail[];
}

// API calls

export async function getVariantGroup(id: number): Promise<VariantGroup> {
    return rms2Fetch<VariantGroup>(`variantgroups/${id}`);
}

export async function getVariantBriefs(): Promise<VariantBrief[]> {
    const response = await rms2Fetch<{ variants: VariantBrief[] }>('variantgroups/variantbriefs');
    return response.variants;
}

export async function getVariantDetails(variantId: number): Promise<VariantDetail[]> {
    const response = await rms2Fetch<{ variantDetails: VariantDetail[] }>(`variantgroups/variantdetails/${variantId}`);
    return response.variantDetails;
}

export async function createVariantGroup(groupName: string, variantId: number, variantDetailIds: number[]): Promise<VariantGroup> {
    return rms2Fetch<VariantGroup>('variantgroups', {
        method: 'POST',
        body: JSON.stringify({ groupName, variantId, variantDetailIds }),
    });
}

export async function updateVariantGroup(
    id: number,
    groupName: string,
    variantDetailIds: number[],
    detailUpdates?: { id: number; statisticName: string }[]
): Promise<VariantGroup> {
    return rms2Fetch<VariantGroup>(`variantgroups/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ groupName, variantDetailIds, detailUpdates }),
    });
}
